"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseCrossChainAdapter = void 0;
const sdk_core_1 = require("@acala-network/sdk-core");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const configs_1 = require("./configs");
const errors_1 = require("./errors");
const types_1 = require("./types");
const DEFAULT_TX_CHECKING_TIMEOUT = 2 * 60 * 1000;
class BaseCrossChainAdapter {
    constructor(chain, routers, tokens) {
        this.chain = chain;
        this.routers = routers;
        this.tokens = tokens;
    }
    async setApi(api) {
        var _a;
        this.api = api;
        if (((_a = this.api) === null || _a === void 0 ? void 0 : _a.type) === "rxjs") {
            await (0, rxjs_1.firstValueFrom)(api.isReady);
        }
        await api.isReady;
    }
    injectFindAdapter(func) {
        this.findAdapter = func;
    }
    getRouters() {
        return this.routers.map((i) => ({
            ...i,
            from: this.chain.id,
        }));
    }
    getSS58Prefix() {
        var _a, _b;
        return Number((_b = (_a = this.api) === null || _a === void 0 ? void 0 : _a.registry.chainSS58) === null || _b === void 0 ? void 0 : _b.toString());
    }
    subscribeInputConfigs(params) {
        const { signer, to, token, address } = params;
        const destFee = this.getCrossChainFee(token, to);
        // subscribe destination min receive
        const minInput$ = this.findAdapter(to)
            .subscribeTokenBalance(token, address)
            .pipe((0, operators_1.map)((destBalance) => destBalance.free
            .add(destBalance.locked)
            .gt(this.getDestED(token, to).balance)), (0, operators_1.mergeMap)((hasExistentialDeposit) => {
            const includeED = !hasExistentialDeposit;
            return this.subscribeMinInput(token, to, includeED);
        }));
        const maxInput$ = this.subscribeMaxInput(token, signer, to);
        const estimateFee$ = this.estimateTxFee({
            ...params,
            amount: new sdk_core_1.FixedPointNumber("10000000000"),
        });
        return (0, rxjs_1.combineLatest)({
            minInput: minInput$,
            maxInput: maxInput$,
            estimateFee: estimateFee$,
        }).pipe((0, operators_1.map)(({ estimateFee, maxInput, minInput }) => {
            return {
                minInput,
                maxInput,
                ss58Prefix: configs_1.chains[to].ss58Prefix,
                destFee,
                estimateFee,
            };
        }));
    }
    subscribeMinInput(token, to, includeExistentialDeposit = true) {
        const destFee = this.getCrossChainFee(token, to);
        const destFeeAmountForToken = destFee.token === token ? destFee.balance : sdk_core_1.FixedPointNumber.ZERO;
        return (0, rxjs_1.of)(destFeeAmountForToken.add(includeExistentialDeposit ? this.getDestED(token, to).balance : sdk_core_1.FixedPointNumber.ZERO));
    }
    getToken(token, chain) {
        let tokenConfig;
        if (!chain)
            return this.tokens[token];
        if (chain === this.chain.id) {
            tokenConfig = this.tokens[token];
        }
        else {
            const destAdapter = this.findAdapter(chain);
            if (!destAdapter) {
                throw new errors_1.AdapterNotFound(token);
            }
            tokenConfig = destAdapter.tokens[token];
        }
        if (!tokenConfig) {
            throw new errors_1.TokenConfigNotFound(token, chain);
        }
        return tokenConfig;
    }
    getNativeToken() {
        var _a, _b;
        if (this.api === undefined) {
            throw new errors_1.ApiNotFound(this.chain.id);
        }
        const nativeToken = this.api.registry.chainTokens[0];
        const decimals = this.api.registry.chainDecimals[0];
        const ed = ((_b = (_a = this.api.consts.balances) === null || _a === void 0 ? void 0 : _a.existentialDeposit) === null || _b === void 0 ? void 0 : _b.toString()) || "0";
        return {
            symbol: nativeToken,
            name: nativeToken,
            decimals,
            ed,
        };
    }
    getDestED(token, destChain) {
        const tokenConfig = this.getToken(token, destChain);
        return {
            token,
            balance: sdk_core_1.FixedPointNumber.fromInner(tokenConfig.ed, tokenConfig.decimals),
        };
    }
    getCrossChainFee(token, destChain) {
        var _a, _b, _c, _d;
        const router = this.routers.find((e) => e.to === destChain && e.token === token);
        if (!router) {
            throw new errors_1.RouterConfigNotFound(token, destChain, this.chain.id);
        }
        const feeToken = ((_b = (_a = router.xcm) === null || _a === void 0 ? void 0 : _a.fee) === null || _b === void 0 ? void 0 : _b.token) || token;
        return {
            token: feeToken,
            balance: sdk_core_1.FixedPointNumber.fromInner(((_d = (_c = router.xcm) === null || _c === void 0 ? void 0 : _c.fee) === null || _d === void 0 ? void 0 : _d.amount) || 0, this.getToken(feeToken, destChain).decimals),
        };
    }
    getDestWeight(token, destChain) {
        var _a;
        const router = this.routers.find((e) => e.to === destChain && e.token === token);
        if (!router) {
            throw new errors_1.RouterConfigNotFound(token, destChain, this.chain.id);
        }
        return (_a = router.xcm) === null || _a === void 0 ? void 0 : _a.weightLimit;
    }
    estimateTxFee(params) {
        var _a;
        let tx = this.createTx({ ...params });
        if (!tx.hasPaymentInfo) {
            // TODO: remove when no longer needed
            // workaround to have at least some fee estimate if paymentInfo is not available
            // return 0.01 UNIT as best guesstimate
            const token = this.getNativeToken();
            const fallbackFeeEstimate = 10 ** (token.decimals - 2);
            return (0, rxjs_1.from)(fallbackFeeEstimate.toString());
        }
        if (((_a = this.api) === null || _a === void 0 ? void 0 : _a.type) === "rxjs") {
            tx = tx;
            return tx.paymentInfo(params.signer).pipe((0, operators_1.map)((feeData) => {
                return feeData.partialFee.toString();
            }));
        }
        // for promise api
        tx = tx;
        return (0, rxjs_1.from)((async () => {
            const feeData = await tx.paymentInfo(params.signer);
            return feeData.partialFee.toString();
        })());
    }
    subscribeBalanceChanged(configs) {
        const { address, amount, token, tolerance } = configs;
        // allow 1% tolerance as default
        const target = amount.mul(new sdk_core_1.FixedPointNumber(1 - (tolerance || 0.01)));
        let savedBalance;
        return this.subscribeTokenBalance(token, address).pipe((0, rxjs_1.timeout)(configs.timeout || DEFAULT_TX_CHECKING_TIMEOUT), (0, operators_1.map)((balance) => {
            if (!savedBalance) {
                savedBalance = balance.available;
            }
            const diff = balance.available.minus(savedBalance);
            if (savedBalance && diff.gte(target)) {
                return types_1.BalanceChangedStatus.SUCCESS;
            }
            return types_1.BalanceChangedStatus.CHECKING;
        }), (0, operators_1.catchError)((e) => {
            if (e instanceof rxjs_1.TimeoutError) {
                return (0, rxjs_1.of)(types_1.BalanceChangedStatus.TIMEOUT);
            }
            return (0, rxjs_1.of)(types_1.BalanceChangedStatus.UNKNOWN_ERROR);
        }));
    }
}
exports.BaseCrossChainAdapter = BaseCrossChainAdapter;
